export {
  FREE_MAX_BYTES,
  PRO_MAX_BYTES,
  getMaxFileSize,
  checkFileSize,
  formatBytes,
  FirewallBlockedError,
  UserCancelledError,
  resetFFmpegEngine,
  stopProcessing,
  loadFFmpeg,
  execWithProgress,
  fetchFile,
  ffmpegOutputToBlob,
  getMediaDurationSeconds as getVideoDurationSeconds,
  probeMedia,
  parseProbeJson,
  type MediaProbe,
} from "@/lib/ffmpegEngine";

const VIDEO_EXTENSIONS = ["mp4", "m4v", "mov", "webm", "mkv", "avi", "wmv", "flv", "3gp", "3g2", "mpg", "mpeg", "ogv", "ts", "mts", "m2ts", "mxf"];

/** Is this plausibly a video file? Checked on drop, so a PDF or a photo is turned away with a clear message. */
export function looksLikeVideo(file: { name: string; type: string }): boolean {
  if ((file.type || "").startsWith("video/")) return true;
  if (file.type && !["application/octet-stream", "application/mp4", "application/x-matroska"].includes(file.type)) return false;
  const ext = (file.name.toLowerCase().match(/\.([^./\\]+)$/) ?? [])[1] ?? "";
  return VIDEO_EXTENSIONS.includes(ext);
}
export type { FileSizeSeverity, FileSizeCheck } from "@/lib/ffmpegEngine";

import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { execWithProgress, ffmpegOutputToBlob, getMediaDurationSeconds } from "@/lib/ffmpegEngine";

const AUDIO_BITRATE_KBPS = 128;
const MIN_VIDEO_BITRATE_KBPS = 100; // below this, quality degrades so much the result usually isn't worth watching

export interface VideoTargetSizeResult {
  blob: Blob;
  size: number;
  metTarget: boolean;
  targetBytes: number;
}

export interface VideoTrim {
  /** Seconds from the start of the file. */
  start: number;
  /** Seconds; omitted means "to the end". */
  end?: number;
}

/** Length in seconds of the part that will be kept; throws a plain-English error when nothing would be left. */
export function trimmedLength(totalSeconds: number, trim?: VideoTrim): number {
  if (!trim) return totalSeconds;
  const end = Math.min(trim.end ?? totalSeconds, totalSeconds);
  const length = end - trim.start;
  if (!(length > 0)) throw new Error("The Start time is at or after the end of the video. Choose an earlier Start or a later End.");
  return length;
}

/**
 * Bitrate to aim at on the next encode after one came out over the target. The real size is used to correct the
 * rate, with a safety margin that grows with each retry, so the last encode lands under the limit.
 */
export function nextAimKbps(kbps: number, size: number, targetBytes: number, attempt: number): number {
  const margin = attempt <= 1 ? 0.97 : 0.93;
  return Math.max(MIN_VIDEO_BITRATE_KBPS, (kbps * (targetBytes * margin)) / size);
}

/** Most encodes tried for one target: a first aim a little under, then up to two corrections from the real size. */
export const MAX_TARGET_ATTEMPTS = 3;

/**
 * Compresses a video toward a target file size with one bitrate-targeted encode, then up to two corrections
 * from the real size. A result counts as meeting the target only when it is at or under it (an upload limit does
 * not accept 25.4 MB for 25 MB). Optional trim keeps only [start, end] and sizes the bitrate for that length.
 */
export async function compressVideoToTarget(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  targetBytes: number,
  onProgress: (fraction: number, attempt?: number) => void,
  signal?: AbortSignal,
  maxHeight?: number,
  trim?: VideoTrim
): Promise<VideoTargetSizeResult> {
  const duration = trimmedLength(await getMediaDurationSeconds(ffmpeg, inputPath), trim);
  const seek = trim && (trim.start > 0 || trim.end !== undefined) ? ["-ss", String(trim.start), "-t", String(duration)] : [];
  // Optional lower resolution: far faster and smaller than squeezing a 1080p picture into a tiny size.
  // fast_bilinear keeps the resize itself cheap in the browser engine.
  const scale = maxHeight ? ["-vf", `scale=-2:'min(${maxHeight},ih)':flags=fast_bilinear`] : [];

  const targetKilobits = (targetBytes * 8) / 1000;
  const totalBitrateKbps = targetKilobits / duration;
  const videoBitrateKbps = Math.max(MIN_VIDEO_BITRATE_KBPS, Math.round(totalBitrateKbps - AUDIO_BITRATE_KBPS));

  let aim = videoBitrateKbps * 0.94;
  let size = 0;
  const spans = [[0, 0.6], [0.6, 0.2], [0.8, 0.2]];
  for (let attempt = 1; attempt <= MAX_TARGET_ATTEMPTS; attempt++) {
    const [lo, span] = spans[attempt - 1];
    const kbps = Math.max(MIN_VIDEO_BITRATE_KBPS, Math.round(aim));
    await execWithProgress(
      ffmpeg,
      [...seek, "-i", inputPath, "-vsync", "cfr", ...scale, "-c:v", "libx264", "-preset", "superfast", "-b:v", `${kbps}k`, "-maxrate", `${Math.round(kbps * 1.1)}k`, "-bufsize", `${kbps * 2}k`, "-pix_fmt", "yuv420p", "-c:a", "aac", "-b:a", `${AUDIO_BITRATE_KBPS}k`, "-movflags", "+faststart", outputPath],
      (f) => onProgress(lo + f * span, attempt),
      signal,
      seek.length ? duration : undefined
    );
    size = ((await ffmpeg.readFile(outputPath)) as Uint8Array).length;
    if (size <= targetBytes || kbps <= MIN_VIDEO_BITRATE_KBPS) break;
    aim = nextAimKbps(kbps, size, targetBytes, attempt);
  }

  const data = await ffmpeg.readFile(outputPath);
  const blob = ffmpegOutputToBlob(data, "video/mp4");
  return { blob, size: blob.size, metTarget: blob.size <= targetBytes, targetBytes }; // strict: a miss is reported as a miss
}
